import { alpha, createTheme, rem } from '@mantine/core'

import { colors } from './colors'

export const theme = createTheme({
  // ─── Brand colors ───────────────────────────────────────────────────────────
  primaryColor: 'primary',
  colors: {
    kudos: [
      '#f4f1ec', // 0 – KUDOS_SURFACE
      '#ece7dc', // 1 – KUDOS_CARD_DIVIDER
      '#eae3d3', // 2 – board bg light
      '#e2dac6', // 3 – board bg dark
      '#c8bca8', // 4
      '#8b8478', // 5 – KUDOS_NEUTRAL
      '#857a63', // 6 – KUDOS_SOFT
      '#3a3733', // 7 – KUDOS_TEXT
      '#362f22', // 8 – KUDOS_INK
      '#262420' // 9 – KUDOS_INK_DEEP
    ],
    primary: [
      '#e8eaf2', // 0 – lightest
      '#c5cade',
      '#a2aacb',
      '#7f8ab7',
      '#5c6aa3',
      '#394a8f',
      '#1e2952', // 6 – main  ← colorPrimary
      '#192241',
      '#131b32',
      '#0d1322' // 9 – darkest
    ],
    success: [
      '#e6faf3',
      '#b3f0d9',
      '#80e6bf',
      '#4ddca5',
      '#26d494',
      '#00d084', // 5 – main  ← colorSuccess
      '#00b873',
      '#009f62',
      '#008752',
      '#006f41'
    ],
    warning: [
      '#fefce8',
      '#fef9c3',
      '#fef08a',
      '#fde047',
      '#facc15',
      '#fbbf24', // 5 – main  ← colorWarning
      '#f59e0b',
      '#d97706',
      '#b45309',
      '#92400e'
    ],
    error: [
      '#fff1f2',
      '#ffe4e6',
      '#fecdd3',
      '#fda4af',
      '#fb7185',
      '#f43f5e',
      '#fb2c36', // 6 – main  ← colorError
      '#e11d48',
      '#be123c',
      '#9f1239'
    ],
    info: [
      '#eff6ff',
      '#dbeafe',
      '#bfdbfe',
      '#93c5fd',
      '#60a5fa',
      '#4a90d9', // 5 – main  ← colorInfo / colorLink
      '#3d7abd', // 6         ← colorLinkHover
      '#2563eb',
      '#1d4ed8',
      '#1e40af'
    ]
  },

  // ─── Typography ─────────────────────────────────────────────────────────────
  fontFamily:
    "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Inter', sans-serif",
  fontSizes: {
    xs: rem(12), // fontSizeSM
    sm: rem(14), // fontSize (base)
    md: rem(14),
    lg: rem(16), // fontSizeLG
    xl: rem(20) // fontSizeXL
  },
  lineHeights: {
    xs: '1.4', // lineHeightSM
    sm: '1.4',
    md: '1.5', // lineHeight (base)
    lg: '1.6', // lineHeightLG
    xl: '1.6'
  },
  headings: {
    fontFamily:
      "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Inter', sans-serif",
    sizes: {
      h1: { fontSize: rem(38), lineHeight: '1.2' }, // fontSizeHeading1
      h2: { fontSize: rem(30), lineHeight: '1.3' }, // fontSizeHeading2
      h3: { fontSize: rem(24), lineHeight: '1.35' }, // fontSizeHeading3
      h4: { fontSize: rem(20), lineHeight: '1.4' }, // fontSizeHeading4
      h5: { fontSize: rem(16), lineHeight: '1.5' }, // fontSizeHeading5
      h6: { fontSize: rem(14), lineHeight: '1.5' }
    }
  },

  // ─── Spacing ─────────────────────────────────────────────────────────────
  spacing: {
    xxs: rem(4), // paddingXXS / marginXXS
    xs: rem(8), // paddingXS  / marginXS
    sm: rem(12), // paddingSM  / marginSM
    md: rem(16), // padding    / margin
    lg: rem(24), // paddingLG  / marginLG
    xl: rem(32),
    xxl: rem(48)
  },

  // ─── Border radius ───────────────────────────────────────────────────────
  radius: {
    xs: rem(6), // borderRadiusXS
    sm: rem(8), // borderRadiusSM
    md: rem(10), // borderRadius (base)
    lg: rem(12), // borderRadiusLG
    xl: rem(16)
  },
  defaultRadius: 'md',

  // ─── Shadows ─────────────────────────────────────────────────────────────
  shadows: {
    xs: '0 1px 2px 0 color-mix(in srgb, black 5%, transparent)', // boxShadow
    sm: '0 4px 6px -1px color-mix(in srgb, black 10%, transparent), 0 2px 4px -1px color-mix(in srgb, black 6%, transparent)', // boxShadowSecondary
    md: '0 1px 3px 0 color-mix(in srgb, black 10%, transparent), 0 1px 2px 0 color-mix(in srgb, black 6%, transparent)', // Card tertiary
    lg: '0 20px 25px 0 color-mix(in srgb, black 10%, transparent), 0 8px 10px 0 color-mix(in srgb, black 10%, transparent)', // Dropdown
    xl: '0 25px 50px -12px color-mix(in srgb, black 25%, transparent)'
  },

  // ─── Transitions ─────────────────────────────────────────────────────────
  other: {
    transitionTimingFunction: 'ease'
  },

  // ─── Other ───────────────────────────────────────────────────────────────
  cursorType: 'pointer',
  focusRing: 'auto',

  // ─── Component overrides ─────────────────────────────────────────────────
  components: {
    // Button ← Button token
    Button: {
      defaultProps: {
        radius: 'sm' // borderRadius: 8
      },
      styles: {
        root: {
          fontWeight: 500,
          boxShadow: 'none'
        }
      }
    },
    Drawer: {
      styles: {
        body: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        },
        content: { backgroundColor: '#1e2952' }
      }
    },
    // TextInput / Input ← Input token
    TextInput: {
      defaultProps: { radius: 'sm' },
      styles: {
        label: {
          marginBottom: rem(4)
        }
      }
    },
    PasswordInput: {
      defaultProps: { radius: 'sm' },
      styles: {
        label: {
          marginBottom: rem(4)
        }
      }
    },
    NumberInput: {
      defaultProps: { radius: 'sm' }
    },
    Textarea: {
      defaultProps: { radius: 'sm' }
    },

    // Select ← Select token
    Select: {
      defaultProps: { radius: 'sm' }
    },
    MultiSelect: {
      defaultProps: { radius: 'sm' }
    },
    Combobox: {
      defaultProps: { radius: 'sm' }
    },

    // Card ← Card token
    Card: {
      defaultProps: {
        radius: 'xl',
        shadow: 'md',
        padding: 'md'
      }
    },

    Paper: {
      defaultProps: {
        radius: 'xl',
        shadow: 'md',
        p: 'md'
      },
      styles: {
        root: {
          borderColor: colors.border
        }
      }
    },

    // Table ← Table token
    Table: {
      defaultProps: { withTableBorder: true, highlightOnHover: true },
      styles: {
        thead: {
          backgroundColor: '#f9fafb',
          color: '#1e2939'
        }
      }
    },

    // Modal ← Modal token
    Modal: {
      defaultProps: { radius: 'lg', centered: true },
      styles: {
        title: {
          fontSize: rem(20),
          lineHeight: '1.4',
          fontWeight: 600
        }
      }
    },

    // Notification ← Notification token
    Notification: {
      defaultProps: { radius: 'md' }
    },

    // Menu ← Menu / Dropdown token
    Menu: {
      defaultProps: { radius: 'md', shadow: 'lg' },
      styles: {
        item: {
          '&[data-hovered]': {
            backgroundColor:
              'color-mix(in srgb, var(--mantine-color-success-5) 5%, transparent)',
            color: '#00d084'
          },
          '&[data-selected]': {
            backgroundColor:
              'color-mix(in srgb, var(--mantine-color-success-5) 10%, transparent)',
            color: '#00d084'
          }
        }
      }
    },

    // NavLink ← Menu (sidebar nav) token
    NavLink: {
      styles: {
        root: {
          padding: '14px 24px',
          borderLeft: '3px solid transparent',
          borderRadius: 0,
          color: alpha(colors.white, 0.7),
          WebkitTapHighlightColor: 'transparent',
          '&:hover': {
            color: colors.white,
            backgroundColor: alpha(colors.white, 0.15)
          },
          '&:active': {
            backgroundColor: alpha(colors.white, 0.2),
            color: colors.white
          }
        }
      }
    },
    // Tabs ← Tabs token
    Tabs: {
      styles: {
        tab: {
          fontSize: rem(14),
          '&[data-active]': {
            color: '#00d084',
            borderColor: '#00d084'
          },
          '&:hover': {
            color: '#00d084'
          }
        }
      }
    },

    // Badge ← Badge token
    Badge: {
      defaultProps: { radius: 'xl' },
      styles: {
        root: { fontSize: rem(10), lineHeight: 1.33 },
        label: { fontSize: rem(10) }
      }
    },

    // Avatar ← Avatar token
    Avatar: {
      defaultProps: { radius: 'xl' },
      styles: {
        root: { width: rem(40), height: rem(40) }
      }
    },

    // Anchor / Link ← colorLink token
    Anchor: {
      styles: {
        root: {
          color: '#4a90d9',
          '&:hover': { color: '#3d7abd' }
        }
      }
    },

    // AppShell ← Layout token
    AppShell: {
      styles: {
        header: { backgroundColor: colors.primary, color: colors.white },
        main: { backgroundColor: colors.white },
        footer: { backgroundColor: colors.white, padding: '24px 50px' }
      }
    },

    //Stack
    Stack: {
      defaultProps: { gap: rem(8) }
    },

    Grid: {
      defaultProps: { p: 0 }
    },

    //Calendar
    Calendar: {
      styles: {
        calendar: {
          width: '100%'
        },
        month: {
          width: '100%'
        },
        levelsGroup: {
          justifyContent: 'center'
        },
        calendarHeader: { display: 'none' },
        monthCell: { padding: 10 },
        day: {
          width: 'clamp(30px, 8vw, 36px)',
          height: 'clamp(30px, 8vw, 36px)',
          borderRadius: '50%',
          fontSize: 'var(--mantine-font-size-xs)',
          fontWeight: 600,
          margin: 'auto'
        },
        cell: {
          padding: '2px'
        },
        weekday: {
          color: alpha(colors.primary, 0.7),
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          paddingBottom: 'sm'
        }
      }
    }
  }
})
